/** Margen mínimo respecto a los bordes del viewport (px). */
export const DATE_PICKER_VIEWPORT_MARGIN = 8;

export const DATE_PICKER_PORTAL_WIDTH = 256;

export interface DatePickerPortalLayoutInput {
  inputRect: DOMRectReadOnly;
  viewportWidth: number;
  viewportHeight: number;
  portalWidth: number;
  portalHeight: number;
  margin?: number;
}

/**
 * Calcula top/left del portal del DatePicker en coordenadas de viewport.
 * Si cabe debajo del input, se ancla ahí con `margin`. Si no, se voltea arriba con el mismo gap.
 */
export function computeDatePickerPortalPosition({
  inputRect,
  viewportWidth,
  viewportHeight,
  portalWidth,
  portalHeight,
  margin = DATE_PICKER_VIEWPORT_MARGIN,
}: DatePickerPortalLayoutInput): { top: number; left: number } {
  const gap = margin;
  const minTop = margin;
  const maxBottom = viewportHeight - margin;

  const topIfBelow = inputRect.bottom + gap;
  const topIfAbove = inputRect.top - portalHeight - gap;
  const fitsBelow = topIfBelow + portalHeight <= maxBottom;

  let top = fitsBelow ? topIfBelow : topIfAbove;

  if (top + portalHeight > maxBottom) {
    top = maxBottom - portalHeight;
  }

  if (top < minTop) {
    top = minTop;
  }

  let left = inputRect.left;

  if (inputRect.left + portalWidth > viewportWidth - margin) {
    left = inputRect.right - portalWidth;
  }

  const minLeft = margin;
  const maxLeft = Math.max(minLeft, viewportWidth - portalWidth - margin);
  left = Math.min(Math.max(left, minLeft), maxLeft);

  return { top, left };
}

export function getEstimatedDatePickerPortalHeight(options: {
  timeVisible: boolean;
  calendarsCount?: number;
  mode?: 'single' | 'range';
}): number {
  const { timeVisible, calendarsCount = 1, mode = 'single' } = options;

  if (mode === 'range' && calendarsCount > 1) {
    return timeVisible ? 520 : 400;
  }

  return timeVisible ? 460 : 320;
}

/** Mide el panel del portal; ignora lecturas inválidas o del tamaño del viewport entero. */
export function measureDatePickerPortalSize(
  portalEl: HTMLElement,
  viewportHeight: number = window.innerHeight
): { width: number; height: number } | null {
  const rect = portalEl.getBoundingClientRect();
  const maxAllowed = viewportHeight - 2 * DATE_PICKER_VIEWPORT_MARGIN;

  if (rect.width <= 0 || rect.height <= 0 || rect.height > maxAllowed) {
    return null;
  }

  return { width: rect.width, height: rect.height };
}

export function isValidPortalHeightForLayout(
  portalHeight: number,
  viewportHeight: number
): boolean {
  const maxAllowed = viewportHeight - 2 * DATE_PICKER_VIEWPORT_MARGIN;
  return portalHeight > 0 && portalHeight <= maxAllowed;
}
